import { LicencedIntervenor } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicencedIntervenor";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { DoctorSearchAutocomplete } from "@/Common/Components/Search/DoctorSearchAutocomplete";
import { LabSearchAutocomplete } from "@/Common/Components/Search/LabSearchAutocomplete";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { ExportableFile } from "../Models/ExportableFile";
import { IFaxInformationProps } from "./FaxAndSignatureLabRequestSelection";

class ClinicianFaxSenderState {
    public cliniciansOnFile: PatientRefIntervenorWithDetailsModel[];
    public selectedClinician: PatientRefIntervenorWithDetailsModel;

    public customClinician: {
        id: string;
        identifier: string;
        name: string;
        permitNumber: string;
        speciality: string;
        address: string;
        phoneNumber: string;
        faxNumber: string;
    };

    public isFaxValid: boolean = false;
    public isPhoneValid: boolean = false;
    public isOtherSelected: boolean = false;

    public constructor(init?: Partial<ClinicianFaxSenderState>) {
        Object.assign(this, init);
    }
}

interface ClinicianFaxSenderProps extends IFaxInformationProps<ExportableFile, PatientRefIntervenorWithDetailsModel[]> {
    useLabsSearch: boolean; // Temporary prop to allow overriding which table to search for. This will be removed when ContactDirectory project is merged.
}

class ClinicianFaxSender extends BaseReactComponent<ClinicianFaxSenderProps, ClinicianFaxSenderState> {
    private phoneValidationRule = new ValidationHelper().getPhoneNumberValidationRule(cacheStore.SiteCache[0].Model.Adresses[0].CountryLookupId, null);
    private validationRegex: string = this.phoneValidationRule ? this.phoneValidationRule.ValidationRegexPattern : "";
    private phoneMask: string = this.phoneValidationRule ? this.phoneValidationRule.EditMaskPattern : "";

    public constructor(props: ClinicianFaxSenderProps) {
        super(props);
        this.state = new ClinicianFaxSenderState({
            cliniciansOnFile: props.defaultContent,
            isOtherSelected: true,
            customClinician: {
                id: "",
                identifier: "",
                name: "",
                permitNumber: "",
                speciality: "",
                address: "",
                phoneNumber: "",
                faxNumber: ""
            }
        });
    }

    public render(): JSX.Element {
        return (
            <div className="single-column centered left-side">
                {this.renderClinicians()}
                {this.renderCustomClinician()}
            </div>
        );
    }

    private onDataChange(): void {
        this.props.onDataChange(
            this.state.isFaxValid && this.state.customClinician.name.trim().length > 0,
            `${this.state.customClinician.name} (${this.state.customClinician.permitNumber})`,
            this.state.customClinician.faxNumber,
            this.props.exportableFile,
            this.state.customClinician.phoneNumber
        );
    }

    public mutateState(mutation: (state: ClinicianFaxSenderState) => void): void {
        super.mutateState(s => mutation(s), () => this.onDataChange?.());
    }

    private renderClinicians(): JSX.Element {
        if (this.state.cliniciansOnFile?.length > 0) {
            const cliniciansOnFileFiltered = this.state.cliniciansOnFile.filter(x => !x.IsFromClinic());
            const clinicians = cliniciansOnFileFiltered.map((x, idx) => this.renderClinicianRow(x, idx));

            return (
                <div className="clinician-block margin-md-left">
                    {clinicians}
                    {this.renderOtherRow()}
                </div>
            );
        }

        return null;
    }

    private renderCustomClinician(): JSX.Element {
        return (
            <div className="flex-container m custom-clinician-block">
                <div className="custom-clinician-table">
                    <table>
                        <tbody>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("NameLabel")}</span>
                                </td>
                                <td className="col2">
                                    {this.props.useLabsSearch
                                        ? (
                                            <LabSearchAutocomplete
                                                componentId="lab_search_component"
                                                onItemSelectCallback={lab => {
                                                    this.mutateState(s => {
                                                        s.customClinician.id = lab.Model.Id.toString();
                                                        s.customClinician.permitNumber = "";
                                                        s.customClinician.identifier = "";
                                                        s.customClinician.name = lab.Model.Name;
                                                        s.customClinician.phoneNumber = lab.GetFormattedPhone();
                                                        s.customClinician.speciality = "";
                                                        s.customClinician.address = lab.getAddress();
                                                        s.customClinician.faxNumber = lab.Model.Fax;
                                                        s.isOtherSelected = true;
                                                        this.setFaxValidity(s);
                                                        this.setPhoneValidity(s);
                                                    });
                                                }}
                                                defaultText={this.state.customClinician.name}
                                                onTextChangedFunction={value => this.mutateState(s => s.customClinician.name = value)}
                                            />
                                        )
                                        : (
                                            <DoctorSearchAutocomplete
                                                componentId="custom_doctor"
                                                searchOnMount={true}
                                                onItemSelectCallback={item => this.onClinicianInfoSelect(item)}
                                                text={this.state.customClinician.name}
                                                textChangedFunction={value => this.mutateState(s => s.customClinician.name = value)}
                                                isValid={this.state.customClinician.name?.trim().length > 0}
                                            />
                                        )
                                    }
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("PermitNumberLabel")}</span>
                                </td>
                                <td className="col2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        disabled={this.props.isSending}
                                        onChange={e => {
                                            const value = e.target.value;
                                            this.mutateState(s => s.customClinician.permitNumber = value);
                                        }}
                                        value={this.state.customClinician.permitNumber}
                                    />
                                </td>
                            </tr>
                            {!cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements && (
                                <>
                                    {this.renderSpecialty()}
                                    {this.renderAddress()}
                                </>
                            )}
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("PhoneNumberLabel")}</span>
                                </td>
                                <td className="col2">
                                    <TextboxMaskedControl
                                        isValid={this.state.isPhoneValid}
                                        allowEmpty={true}
                                        id="phone"
                                        value={this.state.customClinician.phoneNumber}
                                        onChangeFunction={(unmasked, masked) => {
                                            this.mutateState(s => {
                                                s.customClinician.phoneNumber = unmasked;
                                                s = this.setPhoneValidity(s);
                                                if (s.isPhoneValid)
                                                    s.customClinician.phoneNumber = masked;
                                            });
                                        }}
                                        mask={this.phoneMask}
                                        validationErrorMessage=""
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("FaxLabel")}</span>
                                </td>
                                <td className="col2">
                                    <TextboxMaskedControl
                                        isValid={this.state.isFaxValid}
                                        allowEmpty={false}
                                        id="fax"
                                        value={this.state.customClinician.faxNumber}
                                        onChangeFunction={(unmasked, masked) => {
                                            this.mutateState(s => {
                                                s.customClinician.faxNumber = unmasked;
                                                this.setFaxValidity(s);
                                                if (s.isFaxValid)
                                                    s.customClinician.faxNumber = masked;
                                            });
                                        }}
                                        mask={this.phoneMask}
                                        validationErrorMessage=""
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private setPhoneValidity(s: ClinicianFaxSenderState): ClinicianFaxSenderState {
        const regexp = new RegExp(this.validationRegex, "i");
        let valid = (s.customClinician.phoneNumber?.trim().length == 0);
        if (!valid) {
            valid = regexp.test(s.customClinician.phoneNumber);
        }
        s.isPhoneValid = valid;

        return s;
    }

    private onClinicianInfoSelect(item: LicencedIntervenor): void {
        if (item != null) {
            var intervenor = PatientRefIntervenorWithDetailsModel.FromLicencedintervenor(item);
            this.selectIntervenor(intervenor);
        }

        else
            this.clearCustomFields();
    }

    private renderClinicianRow(clinician: PatientRefIntervenorWithDetailsModel, index?: number): JSX.Element {
        if (!cacheStore.ClinicSettings.EnableContactDirectory && !clinician.Model.LicencedIntervenor)
            return null;

        return (
            <div
                className="clinician-row"
                key={"clinician_" + index + "_" + clinician.GetLicence()}
                style={{ display: "flex", alignItems: "center", columnGap: "10px", marginBottom: "10px" }}
            >
                <input
                    type="radio"
                    name="clinician-option"
                    checked={this.state.selectedClinician != null && this.state.selectedClinician.GetDoctorId() == clinician.GetDoctorId()}
                    disabled={this.props.isSending}
                    onChange={() => this.selectIntervenor(clinician, false)}
                />
                <div onClick={() => this.selectIntervenor(clinician, false)}>
                    <div>
                        <b>{clinician.GetName()}</b>
                        <span className="vertical-separator">|</span><b>{cacheStore.GetString("FaxDLabel")}</b> <span>{clinician.GetFax()}</span>
                        <span className="vertical-separator">|</span><b>{cacheStore.GetString("PhoneAbbrLabel")}</b> <span>{clinician.GetPhone()}</span>
                    </div>
                    <div><b>{cacheStore.GetString("AddressLabel")}: </b> <span>{clinician.GetAddress()}</span></div>
                </div>
            </div>
        );
    }

    public clearCustomFields(): void {
        this.mutateState(s => {
            s.selectedClinician = null;
            s.customClinician = {
                id: "",
                identifier: "",
                name: "",
                permitNumber: "",
                speciality: "",
                address: "",
                phoneNumber: "",
                faxNumber: ""
            };
            s.isOtherSelected = true;
            s = this.setPhoneValidity(this.setFaxValidity(s));
        });
    }

    private selectIntervenor(intervenor: PatientRefIntervenorWithDetailsModel, isOtherSelected: boolean = null): void {
        const intervenorInfo = intervenor.Model.LicencedIntervenor;

        this.mutateState(s => {
            s.selectedClinician = intervenor;
            s.customClinician = {
                id: intervenorInfo.Id.toString(),
                permitNumber: intervenor.GetLicence(),
                identifier: intervenor.GetLicence(),
                name: intervenor.GetName(),
                phoneNumber: intervenor.Model.LicencedIntervenor.Phone ?? "",
                speciality: intervenorInfo.SpecialityDetails,
                address: intervenor.GetAddress(),
                faxNumber: intervenor.Model.LicencedIntervenor.Fax ?? ""
            };

            if (isOtherSelected != null)
                s.isOtherSelected = isOtherSelected;

            s = this.setPhoneValidity(this.setFaxValidity(s));
        });
    }

    private setFaxValidity(s: ClinicianFaxSenderState): ClinicianFaxSenderState {
        const regexp = new RegExp(this.validationRegex, "i");
        let valid = regexp.test(s.customClinician.faxNumber);
        valid = valid && s.customClinician.faxNumber?.trim().length > 0;
        s.isFaxValid = valid;
        return s;
    }

    private renderOtherRow(): JSX.Element {
        return (
            <div className="clinician-row" key="clinician_other" style={{ display: "flex", columnGap: "10px" }}>
                <input
                    type="radio"
                    name="clinician-option"
                    checked={this.state.isOtherSelected}
                    disabled={this.props.isSending}
                    onChange={() => this.clearCustomFields()}
                />
                <div
                    className="c2"
                    onClick={() => this.clearCustomFields()}
                >
                    <div className="float-left"><b>{cacheStore.GetString("OtherSingularLabel")}</b></div>
                </div>
            </div>
        );
    }

    private renderSpecialty(): JSX.Element {
        return (
            <tr data-cy="specialtyRow">
                <td className="col1">
                    <span className="float-right"> {cacheStore.GetString("SpecializationLabel")}</span>
                </td>
                <td className="col2">
                    <input
                        type="text"
                        className="form-control"
                        disabled={this.props.isSending}
                        onChange={e => {
                            const value = e.target.value;
                            this.mutateState(s => s.customClinician.speciality = value);
                        }}
                        value={this.state.customClinician.speciality}
                    />
                </td>
            </tr>
        );
    }

    private renderAddress(): JSX.Element {
        return (
            <tr data-cy="addressRow">
                <td className="col1">
                    <span className="float-right"> {cacheStore.GetString("AddressLabel")}</span>
                </td>
                <td className="col2">
                    <input
                        type="text"
                        className="form-control"
                        disabled={this.props.isSending}
                        onChange={e => {
                            const value = e.target.value;
                            this.mutateState(s => s.customClinician.address = value);
                        }}
                        value={this.state.customClinician.address}
                    />
                </td>
            </tr>
        );
    }
}

export { ClinicianFaxSender, ClinicianFaxSenderProps, ClinicianFaxSenderState };
